import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import padStart from 'lodash/padStart';
import { useDispatch } from 'react-redux';
import jsCookie from 'js-cookie';

import ExtendSession from 'components/sessionExpiration/extendSession';
import { unsubscribeComponent } from 'utils/unsubscribeComponent';
import { extendSession, getSessionTimeRemaining } from 'api/user/service';
import { logout as logoutAction } from 'logic/store/authentication/actions';
import { STORAGE_KEYS, ONE_CALCULATED_SECOND_IN_A_DAY, DEFAULT_PROPS } from 'constants.js';

import PopupWindow from './PopupWindow';
import ExpirationView from './ExpirationView';

let sessionWorker;
function setWebWorker(onMessageListener) {
  sessionWorker = new Worker('/workers/timer.js');

  sessionWorker.addEventListener('message', onMessageListener);
}

const windowConfig = {
  features: { width: 480, height: 340 }
};

const THREE_SECONDS = 3000;
const ONE_SECOND = 1000;

const refreshCookie = () => {
  const tabToken = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
  const token = jsCookie.get(STORAGE_KEYS.TOKEN) || tabToken;

  if (!token) {
    return;
  }

  if (!tabToken) {
    sessionStorage.setItem(STORAGE_KEYS.TOKEN, token);
  }

  jsCookie.set(STORAGE_KEYS.TOKEN, token, { path: '/', expires: ONE_CALCULATED_SECOND_IN_A_DAY });
};

const { EMPTY_FUNCTION } = DEFAULT_PROPS;

export const ExpirationWindowComponent = ({
  emulation = false,
  isApplicationTabHidden = false,
  onLogout = EMPTY_FUNCTION
}) => {
  const subscriptions = {
    extendSession: null,
    sessionTimeRemaining: null
  };
  const history = useHistory();
  const dispatch = useDispatch();

  const [show, setShow] = useState(false);
  const [sessionTime, setSessionTime] = useState(null);
  const [sending, setSending] = useState(false);
  const [time, setTime] = useState(null);
  const [sessionExtendDetected, setExtendSession] = useState(moment().valueOf());

  const dispatchLogoutAction = (params) => {
    dispatch(logoutAction(params));
  };

  const logout = (params) => {
    setSending(true);
    dispatchLogoutAction(params);
    onLogout();
  };

  const checkExpiry = ({ inactivityWarningLeadTime, sessionTimeRemaining }) => {
    if (sessionTimeRemaining <= 0) {
      logout({ isSessionExpired: true, history });
    } else if (sending) {
      setShow(false);
    } else {
      const showExpirationWindow = inactivityWarningLeadTime <= THREE_SECONDS;
      setShow(showExpirationWindow);

      if (showExpirationWindow) {
        const duration = moment.duration(sessionTimeRemaining);
        const minutes = padStart(duration.get('minutes'), 2, '0');
        const seconds = padStart(duration.get('seconds'), 2, '0');

        setTime(`${minutes}:${seconds}`);
      }
    }
  };

  const resume = () => {
    setSending(true);
    sessionWorker.postMessage({ action: 'stop' }); // kill worker to stop producing events, and restart again in useEffect

    subscriptions.extendSession = extendSession().subscribe(({ success }) => {
      setShow(false);
      setSending(false);

      if (success) {
        setExtendSession(moment().valueOf());
      }

      if (!success) {
        logout({ isSessionExpired: true, history });
      }
    }, logout);
  };

  const onMessageListener = ({ data: { sessionTime } }) => {
    const sessionTimeData = JSON.parse(sessionTime) ?? {};

    refreshCookie();

    checkExpiry({
      sessionTimeRemaining: Math.max(
        moment(sessionTimeData.sessionTimeRemaining).diff(moment()),
        0
      ),
      inactivityWarningLeadTime: Math.max(
        moment(sessionTimeData.inactivityWarningLeadTime).diff(moment()),
        0
      )
    });
  };

  const onLocalStorageChange = () => {
    const local = JSON.parse(localStorage.getItem(STORAGE_KEYS.EXPIRATION_SESSION_TIME));
    const session = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.EXPIRATION_SESSION_TIME));

    if (local?.sessionTimeRemaining !== session?.sessionTimeRemaining) {
      setSessionTime({
        sessionTimeRemaining: local?.sessionTimeRemaining,
        inactivityWarningLeadTime: local?.inactivityWarningLeadTime
      });
    }
  };

  useEffect(() => {
    setWebWorker(onMessageListener);
    window.addEventListener('storage', onLocalStorageChange);

    return () => {
      window.removeEventListener('storage', onLocalStorageChange);
      sessionWorker.postMessage({ action: 'stop' });
      sessionWorker.removeEventListener('message', onMessageListener);
      sessionWorker.terminate();
      unsubscribeComponent(subscriptions);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isCancelled = false;

    subscriptions.sessionTimeRemaining = getSessionTimeRemaining().subscribe(({ data }) => {
      const sessionInvalid = Boolean(data?.[0]?.code);

      if (sessionInvalid)
        return {
          sessionTimeRemaining: moment(),
          inactivityWarningLeadTime: moment()
        };

      const serverBuffer = moment().add(data.sessionTimeRemaining - 5, 'seconds');

      const updatedTime = {
        sessionTimeRemaining: serverBuffer,
        inactivityWarningLeadTime: serverBuffer
          .clone()
          .subtract(data.inactivityWarningLeadTime, 'seconds')
      };

      localStorage.setItem(STORAGE_KEYS.EXPIRATION_SESSION_TIME, JSON.stringify(updatedTime));
      sessionStorage.setItem(STORAGE_KEYS.EXPIRATION_SESSION_TIME, JSON.stringify(updatedTime));

      if (!isCancelled) setSessionTime(updatedTime);
    });

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionExtendDetected, setSessionTime]);

  useEffect(() => {
    if (sessionTime) {
      sessionWorker.postMessage({ action: 'stop' });
      sessionWorker.postMessage({
        action: 'start',
        timeout: ONE_SECOND,
        sessionTime: localStorage.getItem(STORAGE_KEYS.EXPIRATION_SESSION_TIME)
      });
    }
  }, [sessionTime]);

  ExtendSession({
    isExpirationWindowOpened: show,
    logout: dispatchLogoutAction,
    setExtendSession,
    history
  });

  return (
    <>
      <ExpirationView
        emulation={emulation}
        isApplicationTabHidden={isApplicationTabHidden}
        onLogout={logout}
        onResume={resume}
        sending={sending}
        show={show}
        time={time}
      />

      {isApplicationTabHidden && show && !sending && (
        <PopupWindow {...windowConfig}>
          <ExpirationView
            emulation={emulation}
            isApplicationTabHidden={isApplicationTabHidden}
            onLogout={logout}
            onResume={resume}
            popupMode
            sending={sending}
            show
            time={time}
          />
        </PopupWindow>
      )}
    </>
  );
};

ExpirationWindowComponent.propTypes = {
  onLogout: PropTypes.func,
  isApplicationTabHidden: PropTypes.bool,
  emulation: PropTypes.bool
};

export default ExpirationWindowComponent;
